import React from 'react';
import { useTranslation } from 'react-i18next';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faShieldAlt, faShieldVirus } from '@fortawesome/free-solid-svg-icons';
import { WafInfoDetail } from '../../../types/interfaces';

export interface URLData {
  id: number;
  url: string;
}

interface WafProps {
  selectedUrl: string | null;
  urls: URLData[];
  onUrlChange: (url: string) => void;
  wafData: WafInfoDetail | null;
}

const Waf: React.FC<WafProps> = ({ selectedUrl, urls, onUrlChange, wafData }) => {
  const { t } = useTranslation();

  const handleUrlChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    onUrlChange(e.target.value);
  };

  return (
    <div
      className="bg-white p-6 rounded-lg shadow-lg mb-6 mt-10"
      style={{ minHeight: '500px' }}
    >
      <div className="flex items-center mb-6">
        <div className="w-3 h-10 bg-gray-800 mr-4"></div>
        <h3 className="text-4xl font-bold">{t('waf.title')}</h3>
      </div>

      {selectedUrl && (
        <div className="mt-4 text-gray-700 text-xl">
          <p className="mb-4">{t('waf.description')}</p>

          <p className="text-gray-700 text-xl">{t('waf.select_url')}</p>

          <div className="mb-4 mt-4">
            <select
              id="url-select"
              value={selectedUrl || ''}
              onChange={handleUrlChange}
              className="block w-full max-w-md mt-1 p-2 border border-gray-800 bg-gray-800 text-white font-bold rounded-md shadow-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
            >
              {urls.map((url) => (
                <option key={url.id} value={url.url}>
                  {url.url}
                </option>
              ))}
            </select>
          </div>
        </div>
      )}

      {wafData ? (
        <div className="flex flex-col items-center bg-white p-6 rounded-lg shadow-md mt-4">
          {wafData.detected ? (
            <div className="flex items-center space-x-4 mb-6">
              <FontAwesomeIcon icon={faShieldAlt} className="text-green-500" size="6x" />
              <h4 className="text-6xl font-semibold text-green-600">
                {t('waf.detected')}
              </h4>
            </div>
          ) : (
            <div className="flex items-center space-x-4 mb-6">
              <FontAwesomeIcon icon={faShieldVirus} className="text-red-500" size="3x" />
              <h4 className="text-2xl font-semibold text-red-600">
                {t('waf.not_detected')}
              </h4>
            </div>
          )}

          {wafData.detected && (
            <div className="flex justify-around w-full text-center my-4">
              <p className="text-xl font-semibold">
                <strong>{t('waf.firewall')}:</strong> {wafData.firewall || t('common.unknown')}
              </p>
              <p className="text-xl font-semibold">
                <strong>{t('waf.manufacturer')}:</strong> {wafData.manufacturer || t('common.unknown')}
              </p>
            </div>
          )}

          <div className="mt-6 text-center px-4">
            {wafData.detected ? (
              <p className="text-gray-600">{t('waf.protection_message')}</p>
            ) : (
              <p className="text-red-600">{t('waf.recommendation')}</p>
            )}
          </div>
        </div>
      ) : (
        <p className="text-center text-gray-500">{t('waf.no_info')}</p>
      )}
    </div>
  );
};

export default Waf;
